import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';

/**
 * Order columns a user added to a drill-down whose rows were never fetched.
 *
 * Every other pro grid in the app sends `additional_fields` on its own list
 * request and gets the extra columns back attached to each row. The metrics
 * drill-downs can't: their rows are derived in the browser out of the aggregate
 * payload the page already holds (`serviceFailureDetails(rows)` and friends), so
 * there is no list fetch to hang the names off — and putting them on the metrics
 * request itself would re-run a 92-day scan every time someone adds a column.
 *
 * So this fetches the attached columns ALONE, keyed by pro
 * (`sections=order_fields`), for the caller to merge onto the rows it has. The
 * merge is a plain spread, which means an attached key always wins over a
 * same-named base key — the registry's value for a field is the one every other
 * grid shows, so an added column reads the same here as it does there.
 *
 * `enabled` is the modal's open state: a closed drill-down holds no pros worth
 * asking about, and the pro list changes on every cell click.
 */

export type OrderFieldExtras = Record<string, Record<string, unknown>>;

interface Options {
  /** The drill-down's pros. Order and duplicates don't matter. */
  pros: number[];
  /** Registry fieldnames the user added. Empty means no request is made. */
  additionalFields: string[];
  /** The page's date range. Unused by the lookup itself — the endpoint requires it. */
  range: [string, string] | null;
  enabled: boolean;
}

export interface OrderFieldExtrasResult {
  extras: OrderFieldExtras;
  loading: boolean;
  /** Set when the lookup failed, so the grid can say the columns are missing
   *  rather than render them as empty — a blank cell reads as "no value". */
  error: string | null;
}

export function useOrderFieldExtras(
  { pros, additionalFields, range, enabled }: Options,
): OrderFieldExtrasResult {
  const [extras, setExtras] = useState<OrderFieldExtras>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Both keys are content-derived: the pro list is rebuilt on every render of the
  // host, and an equal list under a new identity must not refetch.
  const prosKey = useMemo(
    () => Array.from(new Set(pros.filter((p) => Number.isFinite(p) && p > 0))).sort((a, b) => a - b).join(','),
    [pros],
  );
  const fieldsKey = additionalFields.join(',');

  useEffect(() => {
    abortRef.current?.abort();
    if (!enabled || !fieldsKey || !prosKey) {
      setExtras({});
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    api
      .get('/reporting/dispatcher-metrics', {
        params: {
          // The range is required by the endpoint and ignored by this section;
          // today both ends keeps it valid when a host has no range to give.
          start_date: range?.[0] ?? new Date().toISOString().slice(0, 10),
          end_date: range?.[1] ?? new Date().toISOString().slice(0, 10),
          sections: 'order_fields',
          background: 'true',   // the page's own request already records the run
          pros: prosKey,
          additional_fields: fieldsKey,
        },
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        const rows: Array<Record<string, unknown>> = res.data?.order_fields?.rows ?? [];
        const next: OrderFieldExtras = {};
        for (const r of rows) {
          const { pro, ...rest } = r as { pro?: unknown } & Record<string, unknown>;
          if (pro != null) next[String(pro)] = rest;
        }
        setExtras(next);
      })
      .catch((e: { name?: string; code?: string }) => {
        if (controller.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setExtras({});
        setError('Added columns could not be loaded');
      })
      .finally(() => {
        if (abortRef.current === controller) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled, prosKey, fieldsKey, range]);

  return { extras, loading, error };
}

export default useOrderFieldExtras;
